import React from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { useTheme } from '@emotion/react';
import {
  FormControl,
  FormHelperText,
  InputBase,
  MenuItem,
  Select,
  type SelectProps,
} from '@mui/material';
import _ from 'lodash';
import { type FunnelLocalState } from '@bbk/retention-sdk/storage';
import { type ExperienceMultiSurveyCardCompetitionSection } from '@bbkAdminRedux/rtkq/experience.slice';
import { FunnelInput } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  CompetitionOtherText,
  type CompetitorOtherTextHandler,
} from '@bbkFunnelComponents/modals/sections/competition-other-text';
import { type SurveyMultiRowResponseHandler } from '@bbkFunnelComponents/survey-multi-row';

const SelectComponent: React.FC<{
  usingPlaceholder: boolean;
  value: string;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    child: React.ReactElement
  ) => void;
  placeholder: string;
  items: React.ReactNode;
}> = ({ usingPlaceholder, value, handleChange, placeholder, items }) => {
  const theme = useTheme() as EmotionTheme;
  const borderColor = new TinyColor(theme.textColor)
    .setAlpha(0.4)
    .toHexString();

  const CssSelectInput = (props: React.ComponentProps<typeof InputBase>) => (
    <InputBase
      {...props}
      sx={{
        '& .MuiInputBase-input': {
          borderBottom: `1px solid ${borderColor}`,
        },
      }}
    />
  );

  const WrappedSelect = (props: SelectProps) => (
    <Select
      {...props}
      sx={{
        '.MuiSelect-icon': {
          color: theme.primaryColor,
        },
      }}
    />
  );

  return (
    <WrappedSelect
      value={value}
      // SelectProps has incorrect types in the library. Props of this component are correct
      onChange={handleChange as SelectProps['onChange']}
      inputProps={{
        name: 'competition',
        id: 'competition',
        sx: {
          color: usingPlaceholder ? theme.primaryColor : theme.textColor,
          opacity: usingPlaceholder ? 1 : 0.5,
        },
      }}
      displayEmpty
      input={<CssSelectInput />}
    >
      <MenuItem value="" disabled className="bbk-competition-menu-item">
        {placeholder}
      </MenuItem>
      {items}
    </WrappedSelect>
  );
};

export type CompetitorOtherTextClearHandler = (cb: () => unknown) => void;

type Props = {
  questions: ExperienceMultiSurveyCardCompetitionSection;
  value: string;
  errorText?: string;
  showOther: boolean;
  compOtherText: string;
  handleCompOtherText: CompetitorOtherTextHandler;
  handler: SurveyMultiRowResponseHandler;
  clearCompOtherText: CompetitorOtherTextClearHandler;
  LS: FunnelLocalState;
  LSNameSpace: string;
  isPreview?: boolean;
};

export const CompetitionContent: React.FC<Props> = ({
  questions,
  value,
  errorText,
  showOther,
  compOtherText,
  handleCompOtherText,
  clearCompOtherText,
  handler,
  isPreview,
  LS: lsProps,
  LSNameSpace: lsNameSpaceProps,
}) => {
  const theme = useTheme() as EmotionTheme;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (_.lowerCase(e.target.value) !== 'other') {
      clearCompOtherText(() => handler(e, e.target.value, 'competition'));
    } else {
      handler(e, e.target.value, 'competition');
    }
  };

  const clearLS = (LS: FunnelLocalState, namespace: string) => {
    const searchRes = LS.search(namespace);
    // clear in all cases except when a history event occurs
    if (searchRes && searchRes.length > 0) {
      if (
        !window.performance ||
        (window.performance &&
          window.performance.navigation &&
          window.performance.navigation.type !== 2)
      ) {
        searchRes.forEach((r) => {
          if (r.key) {
            LS.del(r.key);
          }
        });
      }
    }
  };

  const shuffleOptions = (
    competition: ExperienceMultiSurveyCardCompetitionSection
  ): string[] => {
    let shuffleItems = competition.options.filter(
      (r) => _.lowerCase(r) !== 'other' && _.lowerCase(r) !== 'none'
    );
    const fixedItems = competition.options.filter(
      (r) => _.lowerCase(r) === 'other' || _.lowerCase(r) === 'none'
    );
    if (
      competition.randomize &&
      /[?&]bbk_test=/.test(location.search) &&
      !isPreview
    ) {
      shuffleItems = _.shuffle(shuffleItems);
    }
    return shuffleItems.concat(fixedItems);
  };

  const menuItems = (
    items: string[],
    competition: ExperienceMultiSurveyCardCompetitionSection
  ): React.ReactNode =>
    items.map((r) => (
      <MenuItem key={r} value={r} className="bbk-competition-menu-item">
        {getItemNameForDisplay(competition, r)}
      </MenuItem>
    ));

  const getItemNameForDisplay = (
    competition: ExperienceMultiSurveyCardCompetitionSection,
    option: string
  ): string => {
    const optionObj = competition.options_meta?.[option];
    return optionObj?.display_name ?? option;
  };

  const generateItems = (
    competition: ExperienceMultiSurveyCardCompetitionSection,
    LS: FunnelLocalState,
    LSNameSpace: string
  ) => {
    clearLS(LS, LSNameSpace);
    let items: string[] = [];
    let found = false;
    if (LS.has(LSNameSpace)) {
      const resS = LS.get(LSNameSpace) as string[] | null;
      // IE and Edge handle LS differently, the object check ensures that
      // the correct values are used when necessary
      if (Array.isArray(resS) && !_.isObject(resS[0])) {
        items = [...resS];
        found = true;
      }
    }
    if (!found) {
      items = shuffleOptions(competition);
      LS.put(LSNameSpace, items);
    }
    return menuItems(items, competition);
  };

  const items = generateItems(questions, lsProps, lsNameSpaceProps);
  const placeholder = questions.placeholder || 'Choose one';
  const usingPlaceholder = value !== placeholder && value !== '';

  // console.log('[CompetitionContent items]', items);

  return (
    <div id="competition-content">
      {questions.freeform ? (
        <div className="comp-freeform-text" css={{ marginTop: 10 }}>
          <FunnelInput
            placeholder={placeholder}
            name="competition"
            defaultValue={value}
            onChange={(e) => {
              handleChange(e);
            }}
            onBlur={(e) => handleChange(e)}
            maxLength={140}
          />
        </div>
      ) : (
        <>
          <FormControl fullWidth>
            <SelectComponent
              value={value}
              handleChange={handleChange}
              items={items}
              usingPlaceholder={usingPlaceholder}
              placeholder={placeholder}
            />
          </FormControl>
          <CompetitionOtherText
            question={questions}
            showOther={showOther}
            compOtherText={compOtherText}
            handleOtherText={handleCompOtherText}
          />
        </>
      )}
      {errorText && (
        <FormHelperText style={{ fontSize: 12, color: theme.errorVal }}>
          {errorText}
        </FormHelperText>
      )}
    </div>
  );
};
